import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ThemeContext = createContext(null);
const STORAGE_KEY = "peak:theme";
const CORNERS_KEY = "peak:corners";
const TEXTURE_KEY = "peak:texture";
const PRESETS_KEY = "peak:presets";
const DEFAULT_THEME = "brutalist";
const DEFAULT_CORNERS = "sharp";
const DEFAULT_TEXTURE = "auto"; // = grain on dark themes, none on light
export const CORNER_MODES = ["sharp", "soft"];
export const TEXTURE_MODES = ["auto", "none", "grain", "dots", "grid", "paper"];

export const THEMES = [
  {
    id: "brutalist",
    name: "Peak Brutalist",
    tagline: "Bold Swiss editorial · orange + ink",
    swatches: ["#0A0A0A", "#FF4500", "#F7F7F5", "#FFFFFF"],
    vibe: "Editorial",
  },
  {
    id: "midnight",
    name: "Midnight",
    tagline: "Editorial command-center · navy + muted plum + soft teal",
    swatches: ["#0A1526", "#3E2434", "#7ADBC7", "#EAEFF7"],
    vibe: "Executive",
  },
  {
    id: "sage",
    name: "Sage",
    tagline: "Editorial calm · cream + dusty teal",
    swatches: ["#F4EFE4", "#2F7A6E", "#4A6FA5", "#1E2A25"],
    vibe: "Serif · Calm",
  },
  {
    id: "aurora",
    name: "Aurora",
    tagline: "Editorial dusk · indigo + muted mauve",
    swatches: ["#0D1128", "#C7A2E8", "#7DD3FC", "#ECEBFF"],
    vibe: "AI-first",
  },
  {
    id: "terminal",
    name: "Terminal",
    tagline: "Retro-dev · softened phosphor",
    swatches: ["#05090A", "#3EE07F", "#67E8F9", "#D4EDDB"],
    vibe: "Monospace · Hacker",
  },
];

export const THEME_IDS = THEMES.map((t) => t.id);

function applyTheme(themeId) {
  const id = THEME_IDS.includes(themeId) ? themeId : DEFAULT_THEME;
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("data-theme", id);
  }
  return id;
}

function applyCorners(mode) {
  const m = CORNER_MODES.includes(mode) ? mode : DEFAULT_CORNERS;
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("data-corners", m);
  }
  return m;
}

function applyTexture(mode) {
  const m = TEXTURE_MODES.includes(mode) ? mode : DEFAULT_TEXTURE;
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("data-texture", m);
  }
  return m;
}

export function ThemeProvider({ children }) {
  const { user, refreshMe } = useAuth();
  const [themeId, setThemeId] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const initial = stored && THEME_IDS.includes(stored) ? stored : DEFAULT_THEME;
      applyTheme(initial);
      return initial;
    } catch {
      applyTheme(DEFAULT_THEME);
      return DEFAULT_THEME;
    }
  });
  const [corners, setCornersState] = useState(() => {
    try {
      const stored = localStorage.getItem(CORNERS_KEY);
      const initial = stored && CORNER_MODES.includes(stored) ? stored : DEFAULT_CORNERS;
      applyCorners(initial);
      return initial;
    } catch {
      applyCorners(DEFAULT_CORNERS);
      return DEFAULT_CORNERS;
    }
  });
  const [texture, setTextureState] = useState(() => {
    try {
      const stored = localStorage.getItem(TEXTURE_KEY);
      const initial = stored && TEXTURE_MODES.includes(stored) ? stored : DEFAULT_TEXTURE;
      applyTexture(initial);
      return initial;
    } catch {
      applyTexture(DEFAULT_TEXTURE);
      return DEFAULT_TEXTURE;
    }
  });
  const [presets, setPresets] = useState(() => {
    try {
      const raw = localStorage.getItem(PRESETS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });

  // Whenever we learn the user's preference, adopt it (superadmin's workspace
  // default falls back naturally because the backend sets theme_preference
  // when a user registers into a workspace with a default).
  useEffect(() => {
    if (!user) return;
    const pref = user.theme_preference;
    if (pref && THEME_IDS.includes(pref) && pref !== themeId) {
      setThemeId(applyTheme(pref));
      try { localStorage.setItem(STORAGE_KEY, pref); } catch { /* ignore */ }
    }
  }, [user, themeId]);

  const setTheme = useCallback(async (id, opts = { persist: true }) => {
    const applied = applyTheme(id);
    setThemeId(applied);
    try { localStorage.setItem(STORAGE_KEY, applied); } catch { /* ignore */ }
    if (opts.persist && user) {
      try {
        await api.patch("/auth/theme", { theme: applied });
        if (refreshMe) await refreshMe();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("Failed to persist theme preference", e?.message);
      }
    }
  }, [user, refreshMe]);

  const setCorners = useCallback((mode) => {
    const applied = applyCorners(mode);
    setCornersState(applied);
    try { localStorage.setItem(CORNERS_KEY, applied); } catch { /* ignore */ }
  }, []);

  const setTexture = useCallback((mode) => {
    const applied = applyTexture(mode);
    setTextureState(applied);
    try { localStorage.setItem(TEXTURE_KEY, applied); } catch { /* ignore */ }
  }, []);

  const persistPresets = useCallback((next) => {
    setPresets(next);
    try { localStorage.setItem(PRESETS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  }, []);

  const savePreset = useCallback((name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) return null;
    const preset = {
      id: `p_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      name: trimmed.slice(0, 40),
      theme: themeId,
      corners,
      texture,
    };
    persistPresets([...presets, preset]);
    return preset;
  }, [themeId, corners, texture, presets, persistPresets]);

  const deletePreset = useCallback((id) => {
    persistPresets(presets.filter((p) => p.id !== id));
  }, [presets, persistPresets]);

  const applyPreset = useCallback(async (id) => {
    const p = presets.find((x) => x.id === id);
    if (!p) return;
    if (p.theme && THEME_IDS.includes(p.theme)) {
      setThemeId(applyTheme(p.theme));
      try { localStorage.setItem(STORAGE_KEY, p.theme); } catch { /* ignore */ }
      if (user) {
        try {
          await api.patch("/auth/theme", { theme: p.theme });
          if (refreshMe) await refreshMe();
        } catch (e) { /* ignore */ }
      }
    }
    if (p.corners && CORNER_MODES.includes(p.corners)) {
      applyCorners(p.corners);
      setCornersState(p.corners);
      try { localStorage.setItem(CORNERS_KEY, p.corners); } catch { /* ignore */ }
    }
    if (p.texture && TEXTURE_MODES.includes(p.texture)) {
      applyTexture(p.texture);
      setTextureState(p.texture);
      try { localStorage.setItem(TEXTURE_KEY, p.texture); } catch { /* ignore */ }
    }
  }, [presets, user, refreshMe]);

  const value = useMemo(
    () => ({
      themeId, setTheme, themes: THEMES,
      corners, setCorners,
      texture, setTexture,
      presets, savePreset, applyPreset, deletePreset,
    }),
    [themeId, setTheme, corners, setCorners, texture, setTexture, presets, savePreset, applyPreset, deletePreset]
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
